import type { PerfilVeiculo } from '../didatico/perfilVeiculo'
import type { ProporcoesVitima } from '../didatico/perfisVitima'
import type { MapaEstados } from '../didatico/estadoPecas'
import type { IdVidro, EstadoManejoVidro, ProtecoesManejo } from '../didatico/manejoVidros'
import type { NivelDanoVeicular, RegiaoDanoVeicular } from '../didatico/danoVeicular'
import type { EquipamentoEstabilizacao } from '../didatico/estabilizacao'
import { CORES_TRIAGEM, ORDEM_TRIAGEM, type PrioridadeTriagem } from '../didatico/ocupantes'
import type { FuncaoCorda } from './funcoesCorda'

// A triagem START é canonicamente definida em `didatico/ocupantes.ts` (junto
// com `EstadoClinico`, que Cinemática/Extração continuam usando); o Table
// top só reexporta para quem importa a partir deste barrel.
export { CORES_TRIAGEM, ORDEM_TRIAGEM }
export type { PrioridadeTriagem }

/** Região do corpo da vítima (onde fica uma hemorragia, por exemplo). */
export type RegiaoAnatomicaVitima =
  | 'cabeca'
  | 'torax'
  | 'abdomen'
  | 'membroSuperiorDireito'
  | 'membroSuperiorEsquerdo'
  | 'membroInferiorDireito'
  | 'membroInferiorEsquerdo'
  | 'pelve'
  | 'dorso'
  | 'corpoTodo'

/**
 * Tipos de objeto do Table top.
 *
 * `carro`/`suv` continuam existindo para objetos v1 recém-migrados sem bloco
 * `veiculo` (ver `migracao.ts`); todo objeto novo do tipo veículo já nasce com
 * o bloco preenchido. `bloco` é o bloco de cribbing (mantém o nome do v1); os
 * demais equipamentos de estabilização são tipos próprios.
 */
export const TIPOS_OBJETO_TABLETOP = [
  'carro',
  'suv',
  'moto',
  'caminhao',
  'caminhaoBombeiros',
  'resgate',
  'bombeiro',
  'bombeiroMedico',
  'arvore',
  'poste',
  'defensa',
  'cone',
  'bloco',
  'vitima',
  'vidro',
  'detritosVidro',
  'calco',
  'cunha',
  'vstrut',
  'fitaCatraca',
  'cobertor',
  'lagrima',
  'prancha',
  'muro',
  'derramamento',
  'caboEletrico',
  'areaUsa',
  'manchaSangue',
  'extintorPQS',
  'lonaFerramentas',
  'mochilaTrauma',
  'fogo',
  'escada',
  'toco',
  'maca',
  'polia',
  'mosquetao',
  'bloqueador',
  'talude',
  'barranco',
  'vala',
  'noOito',
  'noFiel',
  'canal',
  'ponte',
  'cisterna',
  'poliaDupla',
  'sistemaVM',
  'trabalhador',
  'torre',
  'pontoAncoragem',
  'placaAncoragem',
  'envolvimento',
  'descensor',
  'protetorCorda',
  'cavalo',
  'vaca',
  'estaca',
] as const

export type TipoObjetoTabletop = (typeof TIPOS_OBJETO_TABLETOP)[number]

/** Tipos que sempre carregam o bloco `veiculo`. */
export const TIPOS_VEICULO_TABLETOP: readonly TipoObjetoTabletop[] = ['carro', 'suv']

/** Tipos de equipamento de estabilização inseríveis no Table top. */
export const TIPOS_EQUIPAMENTO_TABLETOP: readonly TipoObjetoTabletop[] = [
  'calco',
  'cunha',
  'vstrut',
  'fitaCatraca',
  'bloco',
]

export type VetorTabletop = readonly [number, number, number]

export interface TransformacaoTabletop {
  readonly posicao: VetorTabletop
  readonly rotacao: VetorTabletop
  readonly escala: VetorTabletop
}

/**
 * Como o veículo parou — a mesma ideia de `ConfiguracaoVeiculo`, mas com o
 * tombamento desdobrado em dois lados: no Table top o aluno decide para qual
 * lado o carro caiu, algo que o módulo de Extração (um só veículo, um só lado
 * fixo) nunca precisou representar.
 */
export type PosicaoFinalVeiculo =
  | 'sobreRodas'
  | 'tombadoEsquerdo'
  | 'tombadoDireito'
  | 'capotado'
  | 'sobreOutroVeiculo'

export const POSICOES_FINAIS_VEICULO: readonly PosicaoFinalVeiculo[] = [
  'sobreRodas',
  'tombadoEsquerdo',
  'tombadoDireito',
  'capotado',
  'sobreOutroVeiculo',
]

export const IDS_PORTAS_TABLETOP = [
  'portaDianteiraEsquerda', 'portaDianteiraDireita',
  'portaTraseiraEsquerda', 'portaTraseiraDireita',
] as const
export type IdPortaTabletop = (typeof IDS_PORTAS_TABLETOP)[number]

export interface DescritorDeformacaoTabletop {
  readonly regiao: RegiaoDanoVeicular
  readonly nivel: NivelDanoVeicular
  readonly intrusaoM: number
  readonly larguraM?: number
  /** Deslocamento ao longo da face: X para frente/traseira/teto; Z para laterais. */
  readonly centroFaceM?: number
  readonly alturaContatoY?: number
  /** Permite amassar cada porta sem substituir o dano da lateral inteira. */
  readonly idPorta?: IdPortaTabletop
  /** Identificador independente de um amassado aplicado diretamente na superfície. */
  readonly idAmassado?: string
  /** Coordenada Z usada para limitar um amassado no teto. */
  readonly centroLongitudinalM?: number
}

export type EstadoCortePecaTabletop = 'aberta' | 'afastada' | 'removida'

export interface DescritorCorteTabletop {
  readonly idPeca: string
  readonly estado: EstadoCortePecaTabletop
  readonly rotulo?: string
}

export interface BlocoVeiculoTabletop {
  readonly idPerfil: PerfilVeiculo['id']
  readonly posicaoFinal: PosicaoFinalVeiculo
  /** portas abertas, coluna removida, capô afastado etc. — mesmo mapa da maquete didática */
  readonly estadosPecas: MapaEstados
  readonly vidros: Readonly<Partial<Record<IdVidro, EstadoManejoVidro>>>
  readonly insulfilm: boolean
  readonly protecoes: ProtecoesManejo
  readonly dano?: Readonly<{ regiao: RegiaoDanoVeicular; nivel: NivelDanoVeicular; intrusaoM?: number }>
  readonly deformacao?: DescritorDeformacaoTabletop | null
  /** Várias regiões independentes. Ausente em cenas antigas: usar `deformacao`. */
  readonly deformacoes?: readonly DescritorDeformacaoTabletop[]
  readonly cortes?: readonly DescritorCorteTabletop[]
  readonly pecaSelecionada?: string | null
}

export type PosturaVitimaSolo =
  | 'emPe'
  | 'sentada'
  | 'decubitoDorsal'
  | 'decubitoVentral'
  | 'decubitoLateral'

export type LocalVitimaTabletop =
  | Readonly<{ tipo: 'veiculo'; idVeiculo: string; idAncora: string }>
  | Readonly<{ tipo: 'solo'; postura: PosturaVitimaSolo }>

export interface BlocoVitimaTabletop {
  readonly idPerfil: ProporcoesVitima['id']
  readonly triagem: PrioridadeTriagem
  readonly colarCervical: boolean
  /** desempate manual dentro da mesma cor de triagem; menor valor sai primeiro */
  readonly prioridadeManual?: number
  readonly local: LocalVitimaTabletop
  /** membro anatômico com hemorragia externa visível (destacado no 3D) */
  readonly regiaoHemorragia?: RegiaoAnatomicaVitima
}

export interface BlocoEquipamentoTabletop {
  readonly tipo: EquipamentoEstabilizacao
  readonly comCunhaAjuste?: boolean
  readonly rotacaoGraus?: number
  /** Vetor da base no solo até o ponto de apoio no veículo [dx, dy, dz] */
  readonly alcance?: readonly [number, number, number]
}

/** Tipos de objeto que são riscos operacionais da cena (além de decoração). */
export const TIPOS_RISCO_TABLETOP: readonly TipoObjetoTabletop[] = [
  'detritosVidro',
  'derramamento',
  'caboEletrico',
  'manchaSangue',
  'fogo',
]

/** Materiais que a equipe pode jogar sobre uma mancha de óleo/combustível. */
export const MATERIAIS_ABSORVENTES = ['serragem', 'areia', 'absorvente_industrial'] as const
export type MaterialAbsorvente = (typeof MATERIAIS_ABSORVENTES)[number]

/**
 * Configuração de um risco, preparada pelo instrutor. O estado durante a
 * aula (varrido, contido, concessionária acionada...) fica na sessão — nunca
 * aqui —, para reiniciar voltar ao cenário preparado.
 */
export interface BlocoRiscoTabletop {
  /** derramamento: o produto continua vazando até a equipe conter na fonte */
  readonly vazamentoAtivo?: boolean
  /** derramamento: materiais que a regra desta cena aceita; vazio = efeito a definir pelo instrutor */
  readonly materiaisAceitos?: readonly MaterialAbsorvente[]
}

/**
 * Medidas que mudam a forma de um objeto — e, com ela, a posição dos seus
 * pontos de ancoragem (ver `ancoragem.ts`). Só guarda o que o instrutor mudou:
 * o que falta vem do padrão do tipo, e o que sai do intervalo é preso ao limite.
 */
export type ParametrosTabletop = Readonly<Record<string, number>>

export interface ObjetoTabletop {
  readonly id: string
  readonly tipo: TipoObjetoTabletop
  readonly nome: string
  readonly transformacao: TransformacaoTabletop
  readonly cor?: string
  readonly bloqueado?: boolean
  readonly oculto?: boolean
  readonly veiculo?: BlocoVeiculoTabletop
  readonly vitima?: BlocoVitimaTabletop
  readonly equipamento?: BlocoEquipamentoTabletop
  readonly risco?: BlocoRiscoTabletop
  readonly parametros?: ParametrosTabletop
  readonly posicaoDeSoltura?: readonly [number, number, number]
}

/**
 * Uma informação que a central (COBOM) tem no momento do acionamento. O
 * instrutor decide se ela já é confirmada ou só relato do solicitante, e se
 * entra na mensagem lida à guarnição (`visivel`) — pode revelar depois.
 */
export interface ItemAcionamentoCobom {
  readonly id: string
  readonly texto: string
  readonly confirmado: boolean
  readonly visivel: boolean
}

/**
 * Histórico inicial do acionamento, preparado junto com o cenário. Contém só
 * o que a central sabe — nunca número exato de vítimas, lesões ou riscos que
 * a guarnição deve descobrir na cena. Campo vazio fica vazio: a mensagem diz
 * "a confirmar" em vez de inventar o dado.
 */
export interface AcionamentoCobom {
  readonly natureza: string
  readonly local: string
  /** horário simulado do chamado, livre (ex.: "14h32") */
  readonly horario: string
  readonly recursos: string
  readonly informacoes: readonly ItemAcionamentoCobom[]
}

/** Um ponto de ancoragem de um objeto: é isto que a corda guarda, em vez de coordenadas. */
export interface ReferenciaPonto {
  readonly idObjeto: string
  readonly idPonto: string
}

/** Para onde a corda é puxada: a seta fica no último trecho (`paraFim`) ou no primeiro (`paraInicio`). */
export type SentidoCorda = 'paraFim' | 'paraInicio'

/**
 * Uma corda no table top. Guarda os pontos pelos quais passa, em ordem, e não
 * as posições deles: assim acompanha o objeto quando ele se move ou muda de
 * forma. O comprimento e as posições são sempre calculados (ver `cordas.ts`).
 */
export interface CordaTabletop {
  readonly id: string
  readonly funcao: FuncaoCorda
  /** Dois pontos ou mais, na ordem em que a corda passa por eles. */
  readonly percurso: readonly ReferenciaPonto[]
  /** Ausente: sem seta. */
  readonly sentido?: SentidoCorda
  readonly rotulo?: string
  /**
   * opcional: metros de corda a mais em cada trecho (índice i: do ponto i ao
   * i + 1), que deixam o trecho frouxo, pendurado. Ausente ou mais curta que os
   * trechos: os que faltam estão esticados. Ver `folgaCorda.ts`.
   */
  readonly folgas?: readonly number[]
}

/** V prende em dois pontos, W em três e M em quatro; todas as pernas partem do ponto central. */
export type TipoAncoragem = 'V' | 'W' | 'M'

/**
 * Ancoragem em V, W ou M: pontos de fixação ligados a um ponto central, onde a
 * carga se pendura. Guarda só as referências e a carga; o ângulo entre as
 * pernas e a carga por perna são sempre calculados (ver `ancoragens.ts`).
 */
export interface AncoragemTabletop {
  readonly id: string
  readonly tipo: TipoAncoragem
  /** Um ponto por perna: 2 no V, 3 no W, 4 no M. */
  readonly fixacoes: readonly ReferenciaPonto[]
  /** Onde a carga se pendura e para onde as pernas convergem. */
  readonly centro: ReferenciaPonto
  /** Carga no ponto central, em kgf (didática, aproximada). */
  readonly cargaKgf: number
  readonly rotulo?: string
  /** Como as pernas repartem a carga. Ausente: autoequalizável (o comportamento de antes). */
  readonly equalizacao?: EqualizacaoAncoragem
  /**
   * Desvio da direção da carga em relação ao eixo da ancoragem (o oposto da
   * soma das pernas), em graus, no sentido da primeira para a última fixação
   * (negativo: para o outro lado). Ausente: 0.
   */
  readonly desvioCargaGraus?: number
}

/**
 * Autoequalizável: a fita corre no mosquetão do ponto central (X deslizante) e
 * todas as pernas ficam com a mesma tensão. Pré-equalizada: as pernas são
 * amarradas num nó no ponto central e cada uma tem comprimento fixo.
 */
export const EQUALIZACOES_ANCORAGEM = ['autoequalizavel', 'pre-equalizada'] as const
export type EqualizacaoAncoragem = (typeof EQUALIZACOES_ANCORAGEM)[number]

export interface CenaTabletop {
  readonly versao: 2
  readonly nome: string
  readonly historiaInicial?: string
  readonly objetos: readonly ObjetoTabletop[]
  /** opcional: cenas antigas não têm cordas */
  readonly cordas?: readonly CordaTabletop[]
  /** opcional: cenas antigas não têm ancoragens em V, W ou M */
  readonly ancoragens?: readonly AncoragemTabletop[]
  /** opcional: cenas antigas não têm, e a sessão usa o modelo editável */
  readonly acionamento?: AcionamentoCobom
  /**
   * O chão em volta das peças de terreno: a pista de asfalto (padrão) ou mato,
   * para cenários em que a rua fica em cima (no topo de um barranco) e embaixo é encosta.
   */
  readonly chao?: ChaoTabletop
  /**
   * opcional: as marcações dos itens de campo do checklist de trabalho em
   * altura (id do item → feito ou não se aplica). Item sem marcação está pendente.
   */
  readonly checklistAltura?: Readonly<Record<string, MarcacaoChecklistAltura>>
}

export const MARCACOES_CHECKLIST_ALTURA = ['feito', 'nao-se-aplica'] as const
export type MarcacaoChecklistAltura = (typeof MARCACOES_CHECKLIST_ALTURA)[number]

export const CHAOS_TABLETOP = ['asfalto', 'mato'] as const
export type ChaoTabletop = (typeof CHAOS_TABLETOP)[number]

export type NovoObjetoTabletop = Readonly<{
  tipo: TipoObjetoTabletop
  nome?: string
  transformacao?: TransformacaoTabletop
  cor?: string
  veiculo?: BlocoVeiculoTabletop
  vitima?: BlocoVitimaTabletop
  equipamento?: BlocoEquipamentoTabletop
  risco?: BlocoRiscoTabletop
  parametros?: ParametrosTabletop
  posicaoDeSoltura?: readonly [number, number, number]
}>

export type AtualizacoesObjetoTabletop = Readonly<{
  nome?: string
  cor?: string
  bloqueado?: boolean
  oculto?: boolean
  transformacao?: Partial<TransformacaoTabletop>
  /** mesclado raso sobre o bloco existente — não recria os campos omitidos */
  veiculo?: Partial<BlocoVeiculoTabletop>
  vitima?: Partial<BlocoVitimaTabletop>
  equipamento?: Partial<BlocoEquipamentoTabletop>
  /** mescla no bloco de risco; cria o bloco se o objeto ainda não tinha */
  risco?: Partial<BlocoRiscoTabletop>
  /** mescla nos parâmetros existentes; cria o bloco se o objeto ainda não tinha */
  parametros?: ParametrosTabletop
  posicaoDeSoltura?: readonly [number, number, number]
}>

export type GeradorIdTabletop = () => string

